import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/Modal/Modal";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import Dropdown from "../../components/Dropdown/Dropdown";
import InputField from "../../components/InputField/InputField";
import { useAuth } from "../../hooks/useAuth";
import { useUploadFile } from "../../hooks/useUploadFile";
import { useDbConnection } from "../../hooks/useDbConnection";
import { useFileUploadModel } from "../../viewmodels/FileUploadModel";
import { cleanTableName } from "../../utils/tableNameHelper";
import {
  ButtonContainer,
  CancelButton,
  DescriptionField,
  FormContainer,
  FormContent,
  FormLabel,
  FormSection,
  HeaderCircle,
  HeaderContainer,
  HeaderContent,
  HeaderSubtitle,
  HeaderTitle,
  MainContainer,
  NextButton,
  TrySampleLink,
  PreviewContainer,
  PreviewHeader,
  PreviewTitle,
  PreviewTable,
  PreviewTableHead,
  PreviewTableRow,
  PreviewTableHeader,
  PreviewTableCell,
  EditableHeaderInput,
  TableHeaderContainer,
} from "./FileUpload.styles";

const fileTypeOptions = [
  { label: "Excel (.xlsx, .xls)", value: "Excel (.xlsx, .xls)" },
  { label: "CSV", value: "CSV" },
  { label: "TXT", value: "TXT" },
];

// Table icon SVG (same as DbDiagramView)
const TableIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    style={{ marginRight: 6, verticalAlign: "middle" }}
  >
    <rect
      x="2"
      y="4"
      width="16"
      height="12"
      rx="2"
      fill="#fff"
      stroke="#3F49A7"
      strokeWidth="1.5"
    />
    <line x1="2" y1="8" x2="18" y2="8" stroke="#3F49A7" strokeWidth="1.2" />
    <line x1="7" y1="4" x2="7" y2="16" stroke="#3F49A7" strokeWidth="1.2" />
  </svg>
);

// HalfDot component (same as DbDiagramView)
const HalfDot = ({
  side = "left",
  color = "#3F49A7",
  border = "#fff",
  size = 20,
  borderWidth = 3,
  style = {},
}) => {
  return (
    <svg
      width={size}
      height={size}
      style={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        [side]: side === "left" ? -26 : -26,
        zIndex: 3,
        pointerEvents: "auto",
        ...style,
      }}
    >
      <defs>
        <clipPath id={`half-dot-${side}`}>
          {side === "left" ? (
            <rect x={0} y={0} width={size / 2} height={size} />
          ) : (
            <rect x={size / 2} y={0} width={size / 2} height={size} />
          )}
        </clipPath>
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={(size - borderWidth) / 2}
        fill={color}
        stroke={border}
        strokeWidth={borderWidth}
        filter="drop-shadow(0 2px 8px #3F49A720)"
        clipPath={`url(#half-dot-${side})`}
      />
    </svg>
  );
};

// Excel icon SVG (using app colors)
const ExcelIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    style={{ marginRight: 6, verticalAlign: "middle" }}
  >
    <rect
      x="2"
      y="4"
      width="16"
      height="12"
      rx="2"
      fill="#fff"
      stroke="#3F49A7"
      strokeWidth="1.5"
    />
    <line x1="2" y1="8" x2="18" y2="8" stroke="#3F49A7" strokeWidth="1.2" />
    <line x1="7" y1="4" x2="7" y2="16" stroke="#3F49A7" strokeWidth="1.2" />
  </svg>
);

// Custom Node Component with same design as DbDiagramView
const CustomNode = ({ data, id }) => {
  const isExcel = data.type === "excel";
  const headerColor = "#3F49A7"; // Use app color for both Excel and Table nodes
  const icon = isExcel ? <ExcelIcon /> : <TableIcon />;

  return (
    <div
      style={{
        background: "#fff",
        border: `2.5px solid ${headerColor}`,
        borderRadius: 16,
        minWidth: 260,
        boxShadow: `0 6px 24px ${headerColor}20`,
        overflow: "visible",
        fontFamily: "Inter, sans-serif",
        cursor: "grab",
      }}
    >
      <div
        style={{
          background: headerColor,
          color: "#fff",
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          fontWeight: 800,
          fontSize: 20,
          letterSpacing: 1,
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
        }}
      >
        {icon}
        {data.label}
      </div>
      <div style={{ padding: 16 }}>
        {data.columns ? (
          data.columns.map((col, idx) => (
            <div
              key={col.id || idx}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                fontSize: 16,
                padding: "10px 0",
                borderRadius: 8,
                marginBottom: 4,
                position: "relative",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f3f7fa")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <HalfDot side="left" color={headerColor} />
              <Handle
                type="target"
                position={Position.Left}
                id={`target-${id}-${col.id || idx}`}
                style={{
                  top: "50%",
                  transform: "translateY(-50%)",
                  left: -26,
                  position: "absolute",
                  width: 20,
                  height: 20,
                  opacity: 0, // invisible but interactive
                  zIndex: 4,
                  cursor: "pointer",
                }}
              />
              <span style={{ fontWeight: 600, letterSpacing: 0.5 }}>
                {col.name}
              </span>
              <HalfDot side="right" color={headerColor} />
              <Handle
                type="source"
                position={Position.Right}
                id={`source-${id}-${col.id || idx}`}
                style={{
                  top: "50%",
                  transform: "translateY(-50%)",
                  right: -26,
                  position: "absolute",
                  width: 20,
                  height: 20,
                  opacity: 0, // invisible but interactive
                  zIndex: 4,
                  cursor: "pointer",
                }}
              />
            </div>
          ))
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: 16,
              padding: "10px 0",
              borderRadius: 8,
              marginBottom: 4,
              position: "relative",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f7fa")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <HalfDot side="left" color={headerColor} />
            <Handle
              type="target"
              position={Position.Left}
              id={`target-${id}-0`}
              style={{
                top: "50%",
                transform: "translateY(-50%)",
                left: -26,
                position: "absolute",
                width: 20,
                height: 20,
                opacity: 0,
                zIndex: 4,
                cursor: "pointer",
              }}
            />
            <span style={{ fontWeight: 600, letterSpacing: 0.5 }}>
              {data.label}
            </span>
            <HalfDot side="right" color={headerColor} />
            <Handle
              type="source"
              position={Position.Right}
              id={`source-${id}-0`}
              style={{
                top: "50%",
                transform: "translateY(-50%)",
                right: -26,
                position: "absolute",
                width: 20,
                height: 20,
                opacity: 0,
                zIndex: 4,
                cursor: "pointer",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Define node types
const nodeTypes = {
  custom: CustomNode,
};

export default function FileUpload() {
  // Chunked upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isChunkedUploading, setIsChunkedUploading] = useState(false);
  const [showFullPageLoader, setShowFullPageLoader] = useState(false); // NEW: full-page loader
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const previewSectionRef = useRef(null);
  const [postgresTables, setPostgresTables] = useState([]);
  const [postgresLoading, setPostgresLoading] = useState(false);
  const [postgresError, setPostgresError] = useState("");
  const [selectedTableSchema, setSelectedTableSchema] = useState(null);
  const [showFlowDiagram, setShowFlowDiagram] = useState(false);
  const [fullTablesData, setFullTablesData] = useState(null);
  const [showFullFlow, setShowFullFlow] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // React Flow state management
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const {
    uploadFile,
    uploadFileToDb,
    loading: uploadLoading,
    error: uploadError,
  } = useUploadFile();
  const { getPostgresConnectionDetails } = useDbConnection();
  const { user } = useAuth();

  // Use the FileUploadModel - updated to match other viewmodel patterns
  const model = useFileUploadModel(uploadFile, uploadFileToDb);

  // Check if new table name already exists in existing tables
  const tableNameAlreadyExists = useMemo(() => {
    if (model.tableOption !== "new" || !model.tableName?.trim()) {
      return false;
    }
    const newTableNameClean = model.tableName.trim().toLowerCase();
    return postgresTables.some((existingTable) => {
      const existingClean = cleanTableName(existingTable).toLowerCase();
      return existingClean === newTableNameClean;
    });
  }, [model.tableOption, model.tableName, postgresTables]);

  // Debug: Monitor validation state changes
  useEffect(() => {
    const canProceed = model.canProceedToNextStep();
    // console.log("Validation state changed - canProceed:", canProceed);
  }, [
    model.selectedFile,
    model.uploading,
    model.selectedSheet,
    model.tableOption,
    model.tableName,
    model.existingTableSchema,
    model.headerMappings,
  ]);

  // Auto-scroll to preview section when type mismatch error occurs
  useEffect(() => {
    if (
      model.error &&
      model.error.includes("Type mismatch") &&
      previewSectionRef.current
    ) {
      // Small delay to ensure the error message is rendered first
      setTimeout(() => {
        previewSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [model.error]);

  // Fetch Postgres tables for current user (needed for both existing table selection and new table name validation)
  useEffect(() => {
    const fetchTables = async () => {
      // console.log("useEffect triggered - tableOption:", model.tableOption);
      // console.log("User object:", user);
      // console.log("user_id:", user?.user_id);
      // console.log("user.id:", user?.id);
      // console.log("user.userDetails:", user?.userDetails);

      // Try different possible user ID fields
      const userId =
        user?.user_id ||
        user?.id ||
        user?.userDetails?.user_id ||
        user?.userDetails?.id;
      // console.log("Resolved userId:", userId);

      if (!userId) {
        // console.log("No user_id found in any field, skipping fetch");
        return;
      }
      setPostgresLoading(true);
      setPostgresError("");
      try {
        // console.log("Fetching Postgres tables for user:", userId);
        const res = await getPostgresConnectionDetails(userId);
        // console.log("Postgres response:", res);

        if (res.success) {
          let tablesData = res.data?.tablesData;
          // console.log("Raw tablesData:", tablesData);

          // If backend sent JSON string, parse it
          if (typeof tablesData === "string") {
            try {
              tablesData = JSON.parse(tablesData);
              // console.log("Parsed tablesData:", tablesData);
            } catch (e) {
              // console.error("Failed to parse tablesData:", e);
            }
          }
          // Traverse nested structure and extract nodes of type "Table"
          const extractTableNames = (data) => {
            const results = new Set();
            const visit = (node) => {
              if (!node) return;
              if (Array.isArray(node)) {
                node.forEach(visit);
                return;
              }
              if (typeof node === "string") return;
              if (typeof node === "object") {
                if (
                  (node.type === "Table" || node.type === "table") &&
                  node.name
                ) {
                  results.add(String(node.name));
                }
                if (Array.isArray(node.children)) {
                  node.children.forEach(visit);
                }
                // Fallback: traverse values to catch nested structures
                Object.values(node).forEach((v) => {
                  if (v && typeof v === "object") visit(v);
                });
              }
            };
            visit(data);
            return Array.from(results);
          };
          // Extract table names from the exact structure provided
          let names = [];
          if (Array.isArray(tablesData) && tablesData.length > 0) {
            // console.log("tablesData is array with length:", tablesData.length);
            // console.log("First element:", tablesData[0]);

            if (
              tablesData[0]?.children &&
              Array.isArray(tablesData[0].children)
            ) {
              // console.log("First database children:", tablesData[0].children);
              // console.log("Children length:", tablesData[0].children.length);

              // Look for tables in children
              tablesData[0].children.forEach((child, index) => {
                // console.log(`Child ${index}:`, child);
                if (child && child.type === "Table" && child.name) {
                  // console.log(`Found table: ${child.name}`);
                  names.push(child.name);
                }
              });

              // console.log("Extracted table names from children:", names);
            } else {
              // console.log("No children array found in first element");
            }
          } else {
            // console.log("tablesData is not an array or is empty");
          }

          // Filter to only temp_ tables
          names = names.filter(
            (n) => typeof n === "string" && n.startsWith("temp_")
          );
          // console.log("Final filtered temp_ table names:", names);
          setPostgresTables(names);
          setFullTablesData(tablesData); // Store the full data for schema extraction
        } else {
          // console.error("Failed to fetch tables:", res.error);
          setPostgresError(res.error || "Failed to load tables");
          setPostgresTables([]);
        }
      } catch (e) {
        console.error("Error fetching tables:", e);
        setPostgresError(e?.message || "Failed to load tables");
        setPostgresTables([]);
      } finally {
        setPostgresLoading(false);
      }
    };
    fetchTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Function to extract table schema from Postgres response
  const extractTableSchema = useCallback((tablesData, tableName) => {
    if (!Array.isArray(tablesData) || tablesData.length === 0) return null;

    const firstDb = tablesData[0];
    if (!firstDb?.children) return null;

    const table = firstDb.children.find(
      (child) => child.type === "Table" && child.name === tableName
    );

    if (!table?.children) return null;

    return {
      tableName: table.name,
      columns: table.children.map((col) => ({
        name: col.name,
        dataType: col.textDatatype,
        type: col.type,
      })),
    };
  }, []);

  // Function to create React Flow nodes and edges (same layout as DbDiagramView)
  const createFlowData = useCallback(
    (excelHeaders, tableSchema) => {
      const newNodes = [];
      const newEdges = [];

      // Create Excel node with all headers as columns
      const excelNode = {
        id: "excel-source",
        type: "custom",
        position: { x: 100, y: 100 },
        data: {
          label: "Excel Data",
          type: "excel",
          columns: excelHeaders.map((header, index) => ({
            id: `excel-col-${index}`,
            name: header,
            textDatatype: "TEXT",
          })),
        },
        draggable: true,
      };
      newNodes.push(excelNode);

      // Create table node with all columns
      if (tableSchema?.columns) {
        const tableNode = {
          id: "table-target",
          type: "custom",
          position: { x: 500, y: 100 },
          data: {
            label: tableSchema.tableName,
            type: "table",
            columns: tableSchema.columns.map((column, index) => ({
              id: `table-col-${index}`,
              name: column.name,
              textDatatype: column.dataType,
            })),
          },
          draggable: true,
        };
        newNodes.push(tableNode);

        // Create edges for matching headers (one connection per node)
        const usedExcelColumns = new Set();
        const usedTableColumns = new Set();

        excelHeaders.forEach((excelHeader, excelIndex) => {
          if (usedExcelColumns.has(excelIndex)) return; // Skip if already connected

          tableSchema.columns.forEach((column, tableIndex) => {
            if (usedTableColumns.has(tableIndex)) return; // Skip if already connected

            const excelLower = excelHeader.toLowerCase();
            const tableLower = column.name.toLowerCase();

            // Check for exact matches first, then partial matches
            if (
              excelLower === tableLower ||
              excelLower.includes(tableLower) ||
              tableLower.includes(excelLower)
            ) {
              newEdges.push({
                id: `edge-${excelIndex}-${tableIndex}`,
                source: "excel-source",
                target: "table-target",
                sourceHandle: `source-excel-source-excel-col-${excelIndex}`,
                targetHandle: `target-table-target-table-col-${tableIndex}`,
                type: "bezier",
                style: {
                  stroke: "#3F49A7",
                  strokeWidth: 2,
                },
              });

              // Mark both columns as used
              usedExcelColumns.add(excelIndex);
              usedTableColumns.add(tableIndex);
            }
          });
        });
      }

      // Update the state
      setNodes(newNodes);
      setEdges(newEdges);

      return { nodes: newNodes, edges: newEdges };
    },
    [setNodes, setEdges]
  );

  const handleFileInputChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      console.error("No file selected for upload.");
      alert("Please select a file before uploading.");
      return;
    }
    if (file.size === 0) {
      console.error("Selected file is empty.");
      alert("Selected file is empty.");
      return;
    }

    setShowFullPageLoader(true); // Show full-page loader
    await model.handleFileSelect(file);
    setShowFullPageLoader(false); // Hide full-page loader when done
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCancel = () => {
    model.reset();
  };

  // Handle new connections (one-to-one only)
  const onConnect = useCallback(
    (params) => {
      // Check if source or target already has a connection
      const existingEdges = edges.filter(
        (edge) =>
          edge.sourceHandle === params.sourceHandle ||
          edge.targetHandle === params.targetHandle
      );

      if (existingEdges.length > 0) {
        // Remove existing connections for this source or target
        setEdges((eds) =>
          eds.filter(
            (edge) =>
              edge.sourceHandle !== params.sourceHandle &&
              edge.targetHandle !== params.targetHandle
          )
        );
      }

      const newEdge = {
        ...params,
        type: "bezier",
        style: {
          stroke: "#3F49A7",
          strokeWidth: 2,
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges, edges]
  );

  // Handle edge selection (only highlight clicked edge)
  const onEdgeClick = useCallback(
    (event, edge) => {
      event.stopPropagation();
      setEdges((eds) =>
        eds.map((e) => ({
          ...e,
          style: {
            ...e.style,
            strokeWidth: e.id === edge.id ? 4 : 2,
            stroke: e.id === edge.id ? "#ff5722" : "#3F49A7",
          },
        }))
      );
    },
    [setEdges]
  );

  // Handle clicking on empty space to deselect
  const onPaneClick = useCallback(() => {
    setEdges((eds) =>
      eds.map((e) => ({
        ...e,
        style: {
          ...e.style,
          strokeWidth: 2,
          stroke: "#3F49A7",
        },
      }))
    );
  }, [setEdges]);

  // Handle node deletion with keyboard
  const onKeyDown = useCallback(
    (event) => {
      if (event.key === "Delete" || event.key === "Backspace") {
        setNodes((nds) => nds.filter((node) => !node.selected));
      }
    },
    [setNodes]
  );

  // Update nodes and edges when flow data changes
  useEffect(() => {
    if (showFullFlow && selectedTableSchema && model.excelHeaders.length > 0) {
      createFlowData(model.excelHeaders, selectedTableSchema);
    }
  }, [showFullFlow, selectedTableSchema, model.excelHeaders, createFlowData]);

  return (
    <MainContainer>
      {showFullPageLoader && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ color: '#3F49A7', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#3F49A7' }}>Loading file details...</Typography>
          </Box>
        </Box>
      )}
      <FormContainer>
        {/* Header Section */}
        <HeaderContainer>
          <HeaderCircle>1</HeaderCircle>
          <HeaderContent>
            <HeaderTitle>Create Table (Import)</HeaderTitle>
            <HeaderSubtitle>
              Create table(s) by importing data from Excel, CSV, Text files
            </HeaderSubtitle>
          </HeaderContent>
        </HeaderContainer>

        {/* Scrollable Form Content */}
        <FormContent>
          {/* File Upload Section */}
          <FormSection>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              style={{ display: "none" }}
              accept=".csv,.txt,.xlsx"
            />

            {!model.selectedFile ? (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.8,
                  }}
                >
                  <FormLabel>
                    Upload File <span className="required">*</span>
                  </FormLabel>
                </Box>

                <Box
                  sx={{
                    border: "1px dashed #e0e0e0",
                    borderRadius: 1,
                    padding: 2,
                    textAlign: "center",
                    backgroundColor: "#fafafa",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#3F49A7",
                      backgroundColor: "rgba(63, 73, 167, 0.02)",
                    },
                  }}
                  onClick={handleFileUploadClick}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: "rgba(63, 73, 167, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {model.uploading || isChunkedUploading ? (
                        <Box sx={{ width: "100%" }}>
                          <CircularProgress size={16} sx={{ color: "#3F49A7" }} />
                          {isChunkedUploading && (
                            <Typography variant="caption" sx={{ color: "#3F49A7", ml: 1 }}>
                              {uploadProgress}%
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <CloudUploadIcon
                          sx={{ fontSize: 16, color: "#3F49A7" }}
                        />
                      )}
                    </Box>

                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#3F49A7",
                          mb: 0.2,
                        }}
                      >
                        {model.uploading ? "Uploading..." : "Choose File"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "10px",
                          lineHeight: 1.2,
                        }}
                      >
                        Supported: CSV, TXT, XLSX
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box>
                <FormLabel sx={{ mb: 0.8, display: "block" }}>
                  Selected File
                </FormLabel>
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: "rgba(63, 73, 167, 0.05)",
                    borderRadius: 1,
                    border: "1px solid rgba(63, 73, 167, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: 1,
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        backgroundColor: "#3F49A7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          fontSize: "9px",
                        }}
                      >
                        {model.selectedFile.name.split(".").pop().toUpperCase()}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "text.primary",
                          fontSize: "12px",
                          mb: 0.2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {model.selectedFile.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontSize: "10px",
                        }}
                      >
                        {(model.selectedFile.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      model.reset();
                    }}
                    sx={{
                      color: "text.secondary",
                      width: 24,
                      height: 24,
                      "&:hover": {
                        backgroundColor: "rgba(255, 0, 0, 0.1)",
                        color: "error.main",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Sheet Selection */}
            {model.selectedFile &&
              !model.uploading &&
              model.sheets.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Dropdown
                    label="Select Sheet"
                    options={model.sheets.map((sheet) => ({
                      label: sheet,
                      value: sheet,
                    }))}
                    value={model.selectedSheet}
                    onChange={(value) => model.selectSheet(value)}
                    searchable={false}
                    placeholder="Choose a sheet..."
                    variant="default"
                  />
                </Box>
              )}

            {/* Table Option Selection */}
            {model.selectedFile && !model.uploading && model.selectedSheet && (
              <Box sx={{ mt: 2 }}>
                <Dropdown
                  label="Table Option"
                  options={[
                    { label: "New Table", value: "new" },
                    { label: "Existing Table", value: "existing" },
                  ]}
                  value={model.tableOption}
                  onChange={(value) => model.handleTableOptionChange(value)}
                  searchable={false}
                  placeholder="Choose table option..."
                  variant="default"
                />
              </Box>
            )}

            {/* Existing table dropdown (always visible when option selected) */}
            {model.tableOption === "existing" && (
              <Box sx={{ mt: 2 }}>
                <Dropdown
                  label="Select Existing Table"
                  options={(() => {
                    // console.log("postgresTables for dropdown:", postgresTables);
                    const options = (postgresTables || [])
                      .map((t) => {
                        let origName = typeof t === "string" ? t :
                          (t && typeof t === "object" && typeof t.name === "string" ?
                            t.name : null);
                        if (!origName) return null;
                        // Remove starting 'temp_' and trailing '_<timestamp>' for label only
                        const label = cleanTableName(origName);
                        return { label, value: origName };
                      })
                      .filter(Boolean);
                    // console.log("Dropdown options:", options);
                    return options;
                  })()}
                  value={model.tableName}
                  onChange={(value) => {
                    model.setTableName(value);
                    if (value?.trim()) {
                      // Extract table schema from the already fetched data
                      const schema = extractTableSchema(fullTablesData, value);
                      setSelectedTableSchema(schema);

                      // Set the schema in the model for canProceedToNextStep validation
                      if (schema) {
                        // console.log("Setting existing table schema:", schema);
                        model.setExistingTableSchema(schema);
                        // Auto-map headers based on name similarity
                        // console.log(
                        //   "Auto-mapping headers for columns:",
                        //   schema.columns
                        // );
                        model.autoMapHeaders(schema.columns);
                        // console.log(
                        //   "Header mappings after auto-map:",
                        //   model.headerMappings
                        // );
                      }

                      // Show Excel preview when table is selected
                      if (model.excelHeaders.length > 0 && schema) {
                        model.setShowPreview(true);
                        setShowFlowDiagram(false);
                      }
                    }
                  }}
                  loading={postgresLoading}
                  errorText={
                    postgresError ||
                    (postgresTables.length === 0 && !postgresLoading
                      ? "No tables found"
                      : undefined)
                  }
                  searchable={true}
                  placeholder={
                    postgresLoading ? "Loading tables..." : "Choose a table"
                  }
                  variant="default"
                />
              </Box>
            )}

            {/* New table name input (only after file & sheet selected) */}
            {model.tableOption === "new" &&
              model.selectedFile &&
              !model.uploading &&
              model.selectedSheet && (
                <Box sx={{ mt: 2 }}>
                  <InputField
                    label="Table Name"
                    required
                    value={model.tableName}
                    onChange={(e) => {
                      // Only allow alphabets, numbers, and underscores
                      const filteredValue = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                      model.setTableName(filteredValue);
                    }}
                    placeholder="Enter table name..."
                    size="small"
                    fullWidth
                    error={tableNameAlreadyExists}
                  />
                  {tableNameAlreadyExists && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#d32f2f",
                        fontSize: "12px",
                        mt: 0.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      ⚠️ A table with this name already exists. Please choose a different name.
                    </Typography>
                  )}
                </Box>
              )}

            {/* Error Display */}
            {model.error && (
              <Box
                sx={{
                  mt: 2,
                  p: 2.5,
                  bgcolor: "#fff0f0",
                  borderRadius: 1,
                  border: "2px solid #d32f2f",
                  boxShadow: "0 2px 8px rgba(211, 47, 47, 0.15)",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: "#d32f2f",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        fontSize: "14px",
                      }}
                    >
                      !
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#d32f2f",
                        fontWeight: 600,
                        fontSize: "13px",
                        mb: 0.5,
                      }}
                    >
                      Upload Failed
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#c62828",
                        fontSize: "12px",
                        lineHeight: 1.6,
                        mb: 1.5,
                      }}
                    >
                      {model.error}
                    </Typography>
                    {model.dateFormatPrompt &&
                      Array.isArray(model.dateFormatPrompt.columns) &&
                      model.dateFormatPrompt.columns.length > 0 && (
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: "rgba(255, 255, 255, 0.7)",
                            borderRadius: 1,
                            border: "1px solid #ffcdd2",
                            mb: 1.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#b71c1c",
                              fontSize: "11px",
                              fontWeight: 600,
                              mb: 1.25,
                            }}
                          >
                            Enter one date format to use for the whole file (e.g. `YYYY-MM-DD`).
                          </Typography>
                          {(() => {
                            const firstHeader =
                              model.dateFormatPrompt?.columns?.[0]?.header || "Date";
                            const detected = Array.isArray(
                              model.dateFormatPrompt?.columns?.[0]?.detectedFormats
                            )
                              ? model.dateFormatPrompt.columns[0].detectedFormats
                              : [];
                            const current =
                              (model.dateOutputFormats &&
                                model.dateOutputFormats[firstHeader]) ||
                              "";
                            return (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color: "#b71c1c",
                                    fontSize: "10px",
                                    mb: 0.75,
                                  }}
                                >
                                  Detected formats: {detected.join(", ")}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <InputField
                                      value={current}
                                      onChange={(e) =>
                                        model.setDateOutputFormat(
                                          firstHeader,
                                          e.target.value
                                        )
                                      }
                                      placeholder="YYYY-MM-DD"
                                    />
                                  </Box>
                                  <CancelButton
                                    onClick={() => model.setDateFormatPrompt(null)}
                                    sx={{ py: 0.75, px: 2, whiteSpace: "nowrap" }}
                                  >
                                    Cancel
                                  </CancelButton>
                                  <NextButton
                                    onClick={async () => {
                                      const fmt = String(
                                        (model.dateOutputFormats &&
                                          model.dateOutputFormats[firstHeader]) ||
                                          ""
                                      ).trim();
                                      if (!fmt) return;
                                      const success = await model.nextStep();
                                      if (success) setShowSuccessModal(true);
                                    }}
                                    disabled={model.isProcessing}
                                    sx={{ py: 0.75, px: 2, whiteSpace: "nowrap" }}
                                  >
                                    {model.isProcessing ? (
                                      <>
                                        <CircularProgress
                                          size={16}
                                          sx={{ color: "white", mr: 1 }}
                                        />
                                        Processing...
                                      </>
                                    ) : (
                                      "Apply & Retry"
                                    )}
                                  </NextButton>
                                </Box>
                              </>
                            );
                          })()}
                        </Box>
                      )}
                    {model.error.includes("Type mismatch") && (
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "rgba(255, 255, 255, 0.7)",
                          borderRadius: 1,
                          border: "1px solid #ffcdd2",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#b71c1c",
                            fontSize: "11px",
                            fontWeight: 500,
                          }}
                        >
                          💡 <strong>How to fix:</strong> Scroll up to the "Data
                          Preview" section and check the column types. Make sure
                          text columns are set to "Text", number columns to
                          "Integer" or "Decimal", and date columns to "Date".
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </FormSection>

          {/* Data Preview (shown for both New and Existing when enabled) */}
          {model.showPreview && (
            <FormSection ref={previewSectionRef}>
              <PreviewContainer>
                <PreviewHeader>
                  <PreviewTitle>Data Preview</PreviewTitle>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "11px" }}
                  >
                    First 10 rows • {model.excelHeaders.length} columns
                  </Typography>
                </PreviewHeader>
                <Box sx={{ overflowX: "auto", maxHeight: "400px" }}>
                  <PreviewTable>
                    <PreviewTableHead>
                      <PreviewTableRow>
                        {model.excelHeaders.map((header, index) => (
                          <PreviewTableHeader key={index}>
                            <TableHeaderContainer>
                              {model.tableOption === "new" ? (
                                <>
                                  <EditableHeaderInput
                                    value={
                                      model.editableHeaders[index] || header
                                    }
                                    onChange={(e) =>
                                      model.updateHeaderName(
                                        index,
                                        e.target.value
                                      )
                                    }
                                    placeholder={`Column ${index + 1}`}
                                  />
                                  <Box sx={{ width: "100%" }}>
                                    <Dropdown
                                      options={[
                                        { label: "Text", value: "text" },
                                        { label: "Integer", value: "integer" },
                                        { label: "Decimal", value: "decimal" },
                                        { label: "Date", value: "date" },
                                        { label: "Boolean", value: "boolean" },
                                      ]}
                                      value={model.columnTypes[index] || "text"}
                                      onChange={(value) =>
                                        model.updateColumnType(index, value)
                                      }
                                      searchable={false}
                                      placeholder="Select type..."
                                      variant="tableHeader"
                                    />
                                  </Box>
                                </>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{ color: "text.secondary" }}
                                >
                                  {header || `Column ${index + 1}`}
                                </Typography>
                              )}
                            </TableHeaderContainer>
                          </PreviewTableHeader>
                        ))}
                      </PreviewTableRow>
                    </PreviewTableHead>
                    <tbody>
                      {model.previewData.map((row, rowIndex) => (
                        <PreviewTableRow key={rowIndex}>
                          {model.excelHeaders.map((_, colIndex) => (
                            <PreviewTableCell key={colIndex}>
                              {row[colIndex] !== undefined &&
                                row[colIndex] !== null
                                ? String(row[colIndex])
                                : ""}
                            </PreviewTableCell>
                          ))}
                        </PreviewTableRow>
                      ))}
                    </tbody>
                  </PreviewTable>
                </Box>
              </PreviewContainer>
            </FormSection>
          )}

          {/* Header Mapping for Existing Table */}
          {model.showHeaderMapping &&
            model.tableOption === "existing" &&
            !model.showPreview && (
              <FormSection>
                <PreviewContainer>
                  <PreviewHeader>
                    <PreviewTitle>Column Mapping</PreviewTitle>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "11px" }}
                    >
                      Map Excel columns to existing table columns
                    </Typography>
                  </PreviewHeader>

                  {model.fetchingSchema && (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <CircularProgress
                        size={24}
                        sx={{ color: "#3F49A7", mr: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Fetching table schema...
                      </Typography>
                    </Box>
                  )}

                  {model.existingTableSchema && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: "grid", gap: 1.5 }}>
                        {model.excelHeaders.map((excelHeader, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              p: 2,
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <Box sx={{ minWidth: 120, flex: "0 0 auto" }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, color: "text.primary" }}
                              >
                                {excelHeader || `Column ${index + 1}`}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "10px",
                                }}
                              >
                                Excel Column
                              </Typography>
                            </Box>

                            <Box sx={{ flex: "0 0 auto" }}>
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                →
                              </Typography>
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Dropdown
                                options={[
                                  { label: "Skip this column", value: "" },
                                  ...model.existingTableSchema.columns.map(
                                    (col) => ({
                                      label: `${col.name} (${col.dataType})`,
                                      value: col.name,
                                    })
                                  ),
                                ]}
                                value={model.headerMappings[index] || ""}
                                onChange={(value) =>
                                  model.updateHeaderMapping(index, value)
                                }
                                searchable={true}
                                placeholder="Select existing column..."
                                variant="default"
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>

                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: "rgba(63, 73, 167, 0.05)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "11px", color: "text.secondary" }}
                        >
                          <strong>Note:</strong> Only mapped columns will be
                          imported. Unmapped columns will be skipped.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </PreviewContainer>
              </FormSection>
            )}
        </FormContent>

        {/* Action Buttons */}
        <ButtonContainer>
          <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          <NextButton
            onClick={async () => {
              if (model.tableOption === "existing" && selectedTableSchema) {
                // Show full flow diagram for existing table
                setShowFullFlow(true);
                setShowFlowDiagram(false);
              } else {
                // Proceed with normal flow for new table
                const success = await model.nextStep();
                if (success) {
                  // Show success modal only if upload was successful
                  setShowSuccessModal(true);
                }
                // If failed, error will be displayed in the error section
              }
            }}
            disabled={
              !model.canProceedToNextStep() ||
              model.uploading ||
              model.isProcessing ||
              !!model.dateFormatPrompt ||
              tableNameAlreadyExists
            }
            key={`next-button-${model.tableName}-${
              Object.keys(model.headerMappings).length
              }`}
          >
            {model.isProcessing ? (
              <>
                <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
                Processing...
              </>
            ) : (
              "Next"
            )}
          </NextButton>
        </ButtonContainer>
      </FormContainer>

      {/* React Flow Diagram within MainContainer */}
      {showFullFlow && selectedTableSchema && model.excelHeaders.length > 0 && (
        <FormContainer
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header Section - Same as original */}
          <HeaderContainer>
            <HeaderCircle>2</HeaderCircle>
            <HeaderContent>
              <HeaderTitle>Header Mapping</HeaderTitle>
              <HeaderSubtitle>
                {model.selectedFile?.name} → {selectedTableSchema.tableName}
              </HeaderSubtitle>
            </HeaderContent>
          </HeaderContainer>

          {/* Form Content - React Flow */}
          <FormContent
            sx={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <FormSection
              sx={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Box
                sx={{
                  flex: 1,
                  position: "relative",
                  minHeight: 0,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  backgroundColor: "#fafafa",
                }}
              >
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onEdgeClick={onEdgeClick}
                  onPaneClick={onPaneClick}
                  onKeyDown={onKeyDown}
                  nodeTypes={nodeTypes}
                  fitView
                  deleteOnKeyDown={false}
                  connectionLineType="bezier"
                >
                  <Background />
                  <Controls />
                </ReactFlow>
              </Box>

              {/* Legend */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "rgba(63, 73, 167, 0.05)",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: "12px", color: "text.secondary", mb: 1 }}
                >
                  <strong>Legend:</strong> Blue nodes = Excel Data & Database
                  Table, Solid lines = Column Mappings (one-to-one)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "11px", color: "text.secondary" }}
                >
                  <strong>Controls:</strong> Click and drag from column handles
                  to create connections • Each column connects to only one
                  target • Click connections to highlight them • Drag nodes to
                  reposition
                </Typography>
              </Box>
            </FormSection>
          </FormContent>

          {/* Action Buttons - Same as original */}
          <ButtonContainer>
            <CancelButton onClick={() => setShowFullFlow(false)}>
              Previous
            </CancelButton>
            <NextButton
              onClick={async () => {
                // Proceed with the actual upload/processing
                const success = await model.nextStep();
                if (success) {
                  setShowFullFlow(false);
                  // Show success modal only if upload was successful
                  setShowSuccessModal(true);
                } else {
                  // On error, go back to the form to show the error
                  setShowFullFlow(false);
                }
              }}
              disabled={model.isProcessing}
            >
              {model.isProcessing ? (
                <>
                  <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
                  Processing...
                </>
              ) : (
                "Next"
              )}
            </NextButton>
          </ButtonContainer>
        </FormContainer>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <Modal
          type="success"
          title="Upload Successful"
          message="Your file has been successfully uploaded and processed."
          onClose={() => {
            setShowSuccessModal(false);
            navigate("/dashboard", { state: { fromUpload: true } });
          }}
        />
      )}
    </MainContainer>
  );
}
